import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, BadgeCheck, Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { StarRating } from "../components/StarRating";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export default function Reviews() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/comments?limit=100`);
        setComments(data);
      } catch {
        setComments([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const avg = comments.length
    ? (comments.reduce((a, c) => a + c.rating, 0) / comments.length).toFixed(1)
    : null;

  return (
    <div className="pt-24 pb-12 px-6 lg:px-8" data-testid="reviews-page">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="font-heading text-4xl lg:text-5xl font-black">Reviews</h1>
          <p className="text-muted-foreground mt-2 text-lg">What artists are saying about our beats</p>
          {avg && (
            <div className="flex items-center gap-3 mt-4">
              <StarRating value={Math.round(avg)} readOnly size={22} />
              <span className="text-lg font-bold">{avg}</span>
              <span className="text-muted-foreground">from {comments.length} review{comments.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No reviews yet. Visit the store and leave the first one!</p>
            <Link to="/store" className="text-primary hover:underline mt-2 inline-block">Browse Beats</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {comments.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-card border border-border rounded-2xl p-5 flex flex-col"
                data-testid={`review-${c.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{c.name}</span>
                    {c.verified && (
                      <Badge className="bg-green-500/20 text-green-400 gap-1 text-xs">
                        <BadgeCheck className="w-3 h-3" /> Verified Buyer
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDate(c.created_at)}</span>
                </div>
                <StarRating value={c.rating} readOnly size={14} />
                <p className="text-sm text-muted-foreground mt-2 flex-1 whitespace-pre-wrap">{c.text}</p>
                <Link
                  to={`/beat/${c.beat_id}`}
                  className="text-xs text-primary hover:underline mt-3"
                  data-testid={`review-beat-link-${c.id}`}
                >
                  on "{c.beat_title}"
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
